import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RegisterItemList, type ExtraFilter } from './RegisterItemList';
import { raciApi, peopleApi, departmentsApi, evidenceApi, linkedArtefactsApi } from '../services/api';
import { useToast } from '../hooks/useToast';

export const EvidenceList: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();

  const { data: raciAssignments = [] } = useQuery({
    queryKey: ['raci-all'],
    queryFn: () => raciApi.getAll(),
  });
  const { data: people = [] } = useQuery({
    queryKey: ['people-all'],
    queryFn: () => peopleApi.getAll(),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.getAll(),
  });

  const accountableByEvidenceId = useMemo(() => {
    const peopleById = new Map(people.map((p: any) => [p.personId, p]));
    const map = new Map<string, string>();
    for (const a of raciAssignments as any[]) {
      if (a.role !== 'A' || !a.evidenceId) continue;
      const person = peopleById.get(a.personId);
      const name = person ? [person.firstName, person.lastName].filter(Boolean).join(' ').trim() : '';
      map.set(a.evidenceId, name || a.personId);
    }
    return map;
  }, [raciAssignments, people]);

  // Department is derived, not stored: Accountable person -> that person's departmentId -> the
  // real department name. Every Accountable RACI row on this evidence item contributes - today
  // that's just the evidence item's own top-level Accountable, but this reads the full set (not
  // just the first match) so it's already correct once an evidence item can also have
  // submission-scoped RACI rows (submissionId), each with its own Accountable, without needing
  // to touch this derivation again.
  const departmentsByEvidenceId = useMemo(() => {
    const peopleById = new Map(people.map((p: any) => [p.personId, p]));
    const departmentsById = new Map(departments.map((d: any) => [d.departmentId, d]));
    const map = new Map<string, Set<string>>();
    for (const a of raciAssignments as any[]) {
      if (a.role !== 'A' || !a.evidenceId) continue;
      const person: any = peopleById.get(a.personId);
      const dept: any = person?.departmentId ? departmentsById.get(person.departmentId) : null;
      const name = dept?.name || (typeof person?.department === 'string' ? person.department : '');
      if (!name) continue;
      if (!map.has(a.evidenceId)) map.set(a.evidenceId, new Set());
      map.get(a.evidenceId)!.add(name);
    }
    return map;
  }, [raciAssignments, people, departments]);

  const availableDepartments = useMemo(() => {
    const seen: string[] = [];
    for (const names of departmentsByEvidenceId.values()) {
      for (const n of names) if (!seen.includes(n)) seen.push(n);
    }
    return seen.sort();
  }, [departmentsByEvidenceId]);

  // Hide-based, matching the built-in Status pill's convention - a row only disappears once
  // every one of its departments is hidden, so an item spanning e.g. Engineering and Marketing
  // stays visible if only Marketing has been hidden.
  const [hiddenDepartments, setHiddenDepartments] = useState<Set<string>>(new Set());
  const [deptFilterOpen, setDeptFilterOpen] = useState(false);
  const toggleHiddenDepartment = (name: string) => {
    setHiddenDepartments((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const departmentExtraFilter: ExtraFilter = {
    id: 'department',
    predicate: (item) => {
      const names = departmentsByEvidenceId.get(item.evidenceId);
      if (!names || names.size === 0) return true; // no accountable/department resolved - never hidden
      return ![...names].every((n) => hiddenDepartments.has(n));
    },
    control: availableDepartments.length > 0 ? (
      <div className="position-relative">
        <button
          type="button"
          className="np-filter-pill d-flex align-items-center gap-1"
          onClick={() => setDeptFilterOpen((o) => !o)}
        >
          <i className="ri-building-line"></i>
          Department
          {hiddenDepartments.size > 0 && (
            <span className="np-chip np-tone-primary">
              {availableDepartments.length - hiddenDepartments.size}/{availableDepartments.length}
            </span>
          )}
        </button>
        {deptFilterOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1040 }} onClick={() => setDeptFilterOpen(false)} />
            <div className="np-popover">
              <div className="np-popover-head">
                <span>Show department</span>
                {hiddenDepartments.size > 0 && (
                  <button type="button" onClick={() => setHiddenDepartments(new Set())}>
                    Show all
                  </button>
                )}
              </div>
              {availableDepartments.map((name) => (
                <label key={name} className="np-popover-item">
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={!hiddenDepartments.has(name)}
                    onChange={() => toggleHiddenDepartment(name)}
                  />
                  {name}
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    ) : null,
  };

  // Mirrors AssetsPage.tsx's duplicateMutation (RACI re-creation) with one Evidence-specific
  // addition: also re-create Linked Artefacts (Framework + Control links), since for evidence
  // those links are usually the actual point of duplicating - unlike Assets, where RACI alone
  // was "the whole point" per that mutation's own comment. Deliberately does not copy
  // document/S3 attachments, same as Assets - avoids a silent storage-cost surprise, and the
  // action is labelled to say so rather than leaving it ambiguous.
  const duplicateMutation = useMutation({
    mutationFn: async ({ evidenceId: sourceId }: { evidenceId: string; registerId: string }) => {
      const source = await evidenceApi.getOne(sourceId);
      const sourceItem = source.evidence ?? source;
      const sourceTitle = sourceItem.fields?.evidence_title || sourceItem.evidence_title || sourceId;
      const payload = {
        registerId: sourceItem.registerId,
        fields: {
          ...(sourceItem.fields || {}),
          evidence_title: `${sourceTitle} (Copy)`,
        },
      };
      const result = await evidenceApi.create(payload);
      const newEvidenceId = result.evidence?.evidenceId ?? result.evidenceId;

      const raciAssignmentsForItem = await raciApi.getByArtefact('evidence', sourceId);
      let raciCount = 0;
      if (raciAssignmentsForItem.length) {
        const res = await raciApi.batchCreate(
          raciAssignmentsForItem.map((r: any) => ({
            artefactType: 'evidence',
            artefactId: newEvidenceId,
            personId: r.personId,
            role: r.role,
            notes: r.notes,
          })),
        );
        raciCount = res?.created ?? raciAssignmentsForItem.length;
      }

      let linkCount = 0;
      const linksData = await linkedArtefactsApi.getLinks(sourceId, 'evidence');
      const links = (linksData as any)?.links || [];
      if (links.length) {
        const targets = links.map((l: any) => ({ id: l.artefact_id, type: l.artefact_type, register: l.artefact_register }));
        await linkedArtefactsApi.createLinks({
          source_id: newEvidenceId,
          source_type: 'evidence',
          source_register: 'evidence',
          target_artefacts: targets,
        });
        linkCount = targets.length;
      }

      return { newEvidenceId, raciCount, linkCount, registerId: sourceItem.registerId };
    },
    onSuccess: async ({ newEvidenceId, raciCount, linkCount, registerId }) => {
      await qc.invalidateQueries({ queryKey: ['evidence'] });
      const parts = [];
      if (raciCount) parts.push(`${raciCount} RACI assignment${raciCount === 1 ? '' : 's'}`);
      if (linkCount) parts.push(`${linkCount} link${linkCount === 1 ? '' : 's'}`);
      toast.success(`Evidence duplicated${parts.length ? ` with ${parts.join(' and ')}` : ''}. Attachments were not copied.`);
      if (newEvidenceId) window.location.href = `/evidence/${newEvidenceId}?registerId=${encodeURIComponent(registerId || '')}`;
    },
    onError: (e: Error) => toast.error(`Failed to duplicate: ${e.message}`),
  });

  return (
    <RegisterItemList
      title="Evidence"
      iconClass="ri-file-text-line"
      listPath="/evidence"
      detailPathPrefix="/evidence"
      configPath="/evidence-config"
      registersPath="/evidence-registers"
      idField="evidenceId"
      titleField="evidence_title"
      statusField="evidence_status"
      registerQueryParam="registerId"
      extraFilters={[departmentExtraFilter]}
      extraColumns={[
        {
          header: 'Frameworks',
          width: '180px',
          render: (item) => {
            // Sourced from real Linked Artefacts (RUB-50374), not the legacy `fields.frameworkCodes`
            // display string — that field is written by Generate Evidence but was never backed by an
            // actual evidence->framework link, so it could show frameworks that aren't really linked.
            const links: any[] = item.linked_artefacts || [];
            const frameworkLinks = links.filter((l) => l.artefact_type === 'framework');
            if (!frameworkLinks.length) return <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;
            return (
              <div className="d-flex flex-wrap gap-1">
                {frameworkLinks.map((fw) => (
                  <span key={fw.artefact_id} className="np-chip np-tone-primary">{fw.artefact_title}</span>
                ))}
              </div>
            );
          },
        },
        {
          header: 'Controls',
          width: '220px',
          render: (item) => {
            const ids: string[] = item.fields?.controlUserIds || item.controlUserIds || [];
            if (!ids.length) return <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;
            return (
              <div className="d-flex flex-wrap gap-1">
                {ids.map((id: string) => (
                  <span key={id} className="np-chip np-tone-neutral np-tabular">{id}</span>
                ))}
              </div>
            );
          },
        },
        {
          header: 'Accountable',
          width: '180px',
          sortValue: (item) => accountableByEvidenceId.get(item.evidenceId) || '',
          render: (item) => {
            const name = accountableByEvidenceId.get(item.evidenceId);
            return name ? <span>{name}</span> : <span className="text-muted">—</span>;
          },
        },
        {
          header: 'Department',
          width: '180px',
          sortValue: (item) => [...(departmentsByEvidenceId.get(item.evidenceId) || [])].join(', '),
          render: (item) => {
            const names = departmentsByEvidenceId.get(item.evidenceId);
            if (!names || names.size === 0) return <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;
            return (
              <div className="d-flex flex-wrap gap-1">
                {[...names].map((n) => (
                  <span key={n} className="np-chip np-tone-neutral">{n}</span>
                ))}
              </div>
            );
          },
        },
      ]}
      rowActions={(id, _title, registerId) => (
        <button
          type="button"
          className="np-icon-btn"
          title="Duplicate (RACI and framework/control links included, attachments not copied)"
          disabled={duplicateMutation.isPending}
          onClick={() => duplicateMutation.mutate({ evidenceId: id, registerId })}
        >
          <i className="ri-file-copy-line"></i>
        </button>
      )}
    />
  );
};
